import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, LogOut, Users, Trophy, Calendar, FileText, Image as ImageIcon,
  Handshake, MapPin, Archive, Home, Mail, Settings, Edit, Save, AlertCircle,
  Upload, Video, Camera, Lock, Plus, Layers, Palette, Book, Database, Sparkles, HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  siteInfo as mockSite, squadrePulcini2015 as mockSq15, squadrePulcini2016 as mockSq16, squadrePrimiCalci as mockPC,
  campiDaGioco as mockCampi, classifiche as mockCl,
  regolamento as mockReg, sponsors as mockSp,
  categorie as mockCategorie,
  galleriaFoto as mockFoto, galleriaVideo as mockVid, partnerSections as mockPartners,
} from '../data/mock';
import PageBuilder from '../components/admin/PageBuilder';
import ThemeManager from '../components/admin/ThemeManager';
import { ChiSiamoEditor, ContattiEditor, CalendarioEditor, EdizioniEditor } from '../components/admin/ContentEditors';
import { RegolamentoBackendManager, GalleriaBackendManager, VideoBackendManager, SquadreBackendManager, SponsorBackendManager, PasswordChangeForm } from '../components/admin/BackendManagers';
import { HomeBackendManager, CampiBackendManager } from '../components/admin/HomeAndCampi';
import BrochureEditor from '../components/admin/BrochureEditor';
import LayoutEditor from '../components/admin/LayoutEditor';
import TextsEditor from '../components/admin/TextsEditor';
import MediaLibraryPage from '../components/admin/MediaLibraryPage';
import BackupTools from '../components/admin/BackupTools';
import FinaleEditor from '../components/admin/FinaleEditor';
import ConcorsoEditor from '../components/admin/ConcorsoEditor';
import WidgetSistemaHub from '../components/admin/WidgetSistemaHub';
import ClassificheRisultatiManager from '../components/admin/ClassificheRisultatiManager';
import TornaiEditor from '../components/admin/TornaiEditor';
import ChiudiEdizione from '../components/admin/ChiudiEdizione';
import { api } from '../api/client';

// Sezioni visibili in base al ruolo
const allSections = [
  { key: 'home', label: 'Homepage (campi base)', icon: Home, roles: ['admin'] },
  { key: 'widget-hub', label: 'Widget Sistema (dove modifico cosa?)', icon: HelpCircle, roles: ['admin'] },
  { key: 'layout', label: 'Layout pagine', icon: Layers, roles: ['admin'] },
  { key: 'texts', label: 'Testi del sito', icon: FileText, roles: ['admin'] },
  { key: 'media', label: 'Archivio immagini', icon: ImageIcon, roles: ['admin'] },
  { key: 'brochure', label: 'Brochure (homepage)', icon: Book, roles: ['admin'] },
  { key: 'finale', label: 'Serata Finale', icon: Sparkles, roles: ['admin'] },
  { key: 'concorso', label: 'Concorso Disegno', icon: Palette, roles: ['admin'] },
  { key: 'pages', label: 'Pagine dinamiche', icon: Layers, roles: ['admin'] },
  { key: 'theme', label: 'Tema globale', icon: Palette, roles: ['admin'] },
  { key: 'chi-siamo', label: 'Chi Siamo', icon: Users, roles: ['admin'] },
  { key: 'squadre', label: 'Squadre', icon: Trophy, roles: ['admin'] },
  { key: 'classifiche', label: 'Classifiche & Risultati', icon: Trophy, roles: ['admin', 'organizzazione'] },
  { key: 'tornei', label: 'Tornei (gironi · marcatori · cartellini)', icon: Trophy, roles: ['admin', 'organizzazione'] },
  { key: 'calendario', label: 'Calendario', icon: Calendar, roles: ['admin'] },
  { key: 'regolamento', label: 'Regolamento', icon: FileText, roles: ['admin'] },
  { key: 'campi', label: 'Campi', icon: MapPin, roles: ['admin'] },
  { key: 'sponsor', label: 'Sponsor / Partners', icon: Handshake, roles: ['admin'] },
  { key: 'galleria', label: 'Galleria Foto', icon: Camera, roles: ['admin'] },
  { key: 'video', label: 'Video', icon: Video, roles: ['admin'] },
  { key: 'edizioni', label: 'Edizioni Passate', icon: Archive, roles: ['admin'] },
  { key: 'chiudi-edizione', label: '🏁 Chiudi Edizione (archivio auto)', icon: Archive, roles: ['admin'] },
  { key: 'contatti', label: 'Contatti', icon: Mail, roles: ['admin'] },
  { key: 'messaggi', label: 'Messaggi', icon: Mail, roles: ['admin'] },
  { key: 'backup', label: 'Backup / Esporta', icon: Database, roles: ['admin'] },
  { key: 'impostazioni', label: 'Cambio password', icon: Settings, roles: ['admin', 'organizzazione'] },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [active, setActive] = useState('classifiche');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem('adminAuth');
    if (!raw) return navigate('/admin');
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem('adminAuth');
      return navigate('/admin');
    }
    if (!parsed?.token) {
      localStorage.removeItem('adminAuth');
      return navigate('/admin');
    }
    // Validazione SERVER-SIDE del token: blocca chiunque finga un token in localStorage
    let alive = true;
    api.verifyAuth()
      .then((srv) => {
        if (!alive) return;
        // Coerenza ruolo client/server
        if (!srv?.role || (parsed.role && srv.role !== parsed.role)) {
          throw new Error('Ruolo non valido');
        }
        const merged = { ...parsed, role: srv.role };
        setAuth(merged);
        setActive(srv.role === 'organizzazione' ? 'classifiche' : 'home');
        setVerifying(false);
      })
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem('adminAuth');
        toast.error('Sessione scaduta, effettua di nuovo l\'accesso');
        navigate(parsed.role === 'organizzazione' ? '/organizzazione' : '/admin');
      });
    return () => { alive = false; };
  }, [navigate]);

  const logout = () => {
    const role = auth?.role;
    localStorage.removeItem('adminAuth');
    toast.success('Logout effettuato');
    navigate(role === 'organizzazione' ? '/organizzazione' : '/admin');
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-pink-brand text-sm font-semibold inline-flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-pink-brand border-t-transparent rounded-full animate-spin" />
          {verifying ? 'Verifica accesso…' : 'Reindirizzamento…'}
        </div>
      </div>
    );
  }

  const sections = allSections.filter((s) => s.roles.includes(auth.role));
  const messages = JSON.parse(localStorage.getItem('contactMessages') || '[]');

  return (
    <div className="min-h-screen bg-navy flex">
      <aside className={`fixed lg:static z-30 w-64 bg-navy-dark border-r border-white/10 min-h-screen flex flex-col transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src={mockSite.logo} alt="logo" className="w-11 h-11 object-contain" />
          <div>
            <div className="font-brand text-white font-bold text-sm">Admin Panel</div>
            <div className="text-pink-brand text-xs font-semibold">{auth.label}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button key={s.key} onClick={() => { setActive(s.key); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold mb-1 transition-colors ${active === s.key ? 'bg-pink-brand text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}>
                <Icon size={17} />
                {s.label}
                {s.key === 'messaggi' && messages.length > 0 && (
                  <span className="ml-auto bg-white text-pink-brand text-[10px] font-bold px-2 py-0.5 rounded-full">{messages.length}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" target="_blank" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:bg-white/5 hover:text-white">
            <LayoutDashboard size={17} /> Vai al sito
          </Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:bg-red-500/15 hover:text-red-400">
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 lg:ml-0 p-4 lg:p-8 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between mb-6 lg:mb-8">
          <div>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden mb-2 text-white bg-navy-dark px-3 py-2 rounded-lg">Menu</button>
            <h1 className="font-display text-3xl font-bold text-white uppercase">{sections.find((s) => s.key === active)?.label}</h1>
            <p className="text-white/60 text-sm mt-1">
              {auth.role === 'organizzazione' ? 'Accesso esclusivo · Gestione risultati e classifiche' : 'Gestisci i contenuti del sito'}
            </p>
          </div>
        </div>

        {active === 'home' && <HomeBackendManager />}
        {active === 'layout' && <LayoutEditor />}
        {active === 'texts' && <TextsEditor />}
        {active === 'media' && <MediaLibraryPage />}
        {active === 'brochure' && <BrochureEditor />}
        {active === 'finale' && <FinaleEditor />}
        {active === 'concorso' && <ConcorsoEditor />}
        {active === 'widget-hub' && (
          <WidgetSistemaHub onNavigate={(key) => {
            const map = { home: 'home', layout: 'layout', testi: 'texts', media: 'media', brochure: 'brochure', finale: 'finale', concorso: 'concorso', sponsor: 'sponsor', partner: 'sponsor', squadre: 'squadre', calendario: 'calendario', classifiche: 'classifiche', regolamento: 'regolamento', galleria: 'galleria', video: 'video', edizioni: 'edizioni', chisiamo: 'chi-siamo', contatti: 'contatti', tema: 'theme', pages: 'pages', campi: 'home' };
            const target = map[key] || key;
            setActive(target);
            setSidebarOpen(false);
          }} />
        )}
        {active === 'pages' && <PageBuilder />}
        {active === 'theme' && <ThemeManager />}
        {active === 'chi-siamo' && <ChiSiamoEditor />}
        {active === 'squadre' && <SquadreBackendManager categorie={mockCategorie} />}
        {active === 'classifiche' && <ClassificheRisultatiManager role={auth.role} />}
        {active === 'tornei' && <TornaiEditor />}
        {active === 'calendario' && <CalendarioEditor />}
        {active === 'regolamento' && <RegolamentoBackendManager />}
        {active === 'campi' && <CampiBackendManager />}
        {active === 'sponsor' && <SponsorBackendManager />}
        {active === 'galleria' && <GalleriaBackendManager />}
        {active === 'video' && <VideoBackendManager />}
        {active === 'edizioni' && <EdizioniEditor />}
        {active === 'chiudi-edizione' && <ChiudiEdizione />}
        {active === 'contatti' && <ContattiEditor />}
        {active === 'messaggi' && <MessaggiManager msgs={messages} />}
        {active === 'backup' && <BackupTools />}
        {active === 'impostazioni' && <Impostazioni />}
      </main>
    </div>
  );
}

function Card({ children }) { return <div className="glass-card p-5 lg:p-6">{children}</div>; }

function UploadSlot({ label = 'Carica immagine', accept = 'image/*' }) {
  return (
    <div>
      <label className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2 block">{label}</label>
      <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-pink-brand transition-colors cursor-pointer">
        <Upload size={24} className="text-pink-brand mx-auto mb-2" />
        <p className="text-white/70 text-sm">Trascina un file qui o clicca per caricare</p>
        <p className="text-white/40 text-xs mt-1">{accept}</p>
        <input type="file" accept={accept} className="hidden" />
      </div>
    </div>
  );
}

function HomeManager() {
  return (
    <div className="space-y-5">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-lg">Informazioni Edizione Attuale</h2>
          <button className="btn-primary !py-2 !px-5"><Save size={16} /> Salva</button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[{ l: 'Edizione', v: 'XIV Memorial' }, { l: 'Titolo', v: 'Eugenio Abbro' }, { l: 'Date Range', v: '25 / 31 Maggio 2026' }, { l: 'Anno', v: '2026' }].map((f) => (
            <div key={f.l}>
              <label className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2 block">{f.l}</label>
              <input type="text" defaultValue={f.v} className="w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Media Hero</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <UploadSlot label="Video sfondo home" accept="video/mp4" />
          <UploadSlot label="Immagine fallback (poster)" accept="image/*" />
          <UploadSlot label="Video di presentazione" accept="video/mp4" />
          <UploadSlot label="Brochure PDF" accept="application/pdf" />
        </div>
      </Card>
      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Patrocini</h2>
        <div className="space-y-3">
          <div>
            <label className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2 block">Titolo</label>
            <input type="text" defaultValue="con il patrocinio di" className="w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <UploadSlot label="Immagine unica con tutti i patrocini (PNG trasparente)" accept="image/png,image/svg+xml" />
        </div>
      </Card>
      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Logo</h2>
        <UploadSlot label="Logo principale A.S.D.P." accept="image/png,image/jpeg,image/svg+xml" />
      </Card>
    </div>
  );
}

function SquadreManager({ sq15, sq16, sqPC, categorie }) {
  const [tab, setTab] = useState('pulcini-2015');
  const map = { 'pulcini-2015': sq15, 'pulcini-2016': sq16, 'primi-calci': sqPC };
  const list = map[tab] || [];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2 bg-navy-dark p-1.5 rounded-full border border-white/10 w-fit">
        {categorie.map((c) => (
          <button key={c.key} onClick={() => setTab(c.key)} className={`py-2 px-5 rounded-full font-bold text-xs uppercase ${tab === c.key ? 'bg-pink-brand text-white' : 'text-white/70'}`}>
            {c.label}
          </button>
        ))}
      </div>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">Squadre ({list.length})</h2>
          <button className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Aggiungi</button>
        </div>
        <div className="space-y-3">
          {list.map((s) => (
            <div key={s.id} className="p-4 bg-navy-dark rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold">{s.name}</div>
                  <div className="text-white/60 text-xs">Girone {s.girone} · Sponsor: {s.sponsor}</div>
                </div>
                <button className="p-2 text-white/60 hover:text-pink-brand"><Edit size={16} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-white/50 font-bold uppercase tracking-widest mb-1">Foto squadra</div>
                  <UploadSlot label="" />
                </div>
                <div>
                  <div className="text-white/50 font-bold uppercase tracking-widest mb-1">Rosa giocatori</div>
                  <textarea defaultValue={s.giocatori} rows={3} className="w-full bg-navy border border-white/10 rounded p-2 text-white text-xs" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ClassificheManager({ data, role }) {
  return (
    <div className="space-y-4">
      {role === 'organizzazione' && (
        <Card>
          <div className="flex items-start gap-3 text-white/85">
            <Lock className="text-pink-brand flex-shrink-0" size={18} />
            <div className="text-sm">
              <strong>Accesso Organizzazione</strong> — puoi inserire e aggiornare i risultati di tutte le partite e le classifiche finali. Gli altri contenuti del sito sono gestiti dall'Amministratore.
            </div>
          </div>
        </Card>
      )}
      <Card>
        <div className="space-y-5">
          {Object.entries(data).map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold">{v.categoria}</h3>
                <button className="btn-primary !py-2 !px-4 !text-xs"><Plus size={14} /> Aggiungi squadra</button>
              </div>
              {v.ranking && v.ranking.length > 0 ? (
                <div className="space-y-1">
                  {v.ranking.map((r) => (
                    <div key={r.pos} className="flex items-center gap-3 p-2 bg-navy-dark rounded">
                      <span className="w-8 h-8 rounded-full bg-pink-brand text-white font-bold text-sm flex items-center justify-center">{r.pos}</span>
                      <input defaultValue={r.team} className="flex-1 bg-transparent border-b border-white/10 text-white text-sm py-1 focus:outline-none focus:border-pink-brand" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-navy-dark rounded text-white/60 text-sm">Classifica vuota · aggiungi squadre con i punti a fine torneo</div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function CalendarioManager({ data }) {
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold">{d.giorno} {d.data}</h3>
            <button className="text-pink-brand text-sm font-bold"><Plus size={14} className="inline" /> Evento</button>
          </div>
          <div className="space-y-2">
            {d.eventi.map((e, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 bg-navy-dark rounded text-sm">
                <span className="text-pink-brand font-bold w-14">{e.ora}</span>
                <span className="text-white flex-1">{e.evento}</span>
                <span className="text-white/60 hidden sm:block">{e.luogo}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function RegolamentoManager({ data }) {
  return (
    <div className="space-y-3">
      {data.map((art, i) => (
        <Card key={i}>
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-lg bg-pink-brand text-white font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <input defaultValue={art.titolo} className="w-full bg-transparent text-white font-bold border-b border-white/10 pb-1 mb-2 focus:outline-none focus:border-pink-brand" />
              <textarea defaultValue={art.testo} rows={4} className="w-full bg-navy-dark border border-white/10 rounded p-2 text-white/80 text-sm" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function CampiManager({ data }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {data.map((c) => (
        <Card key={c.id}>
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="text-pink-brand" size={20} />
            <input defaultValue={c.nome} className="flex-1 bg-transparent text-white font-bold border-b border-white/10 pb-1 focus:outline-none focus:border-pink-brand" />
          </div>
          <input defaultValue={c.indirizzo} className="w-full bg-navy-dark border border-white/10 rounded px-3 py-2 text-white/80 text-sm mb-2" />
          <input defaultValue={c.tipo} className="w-full bg-navy-dark border border-white/10 rounded px-3 py-2 text-white/80 text-sm" />
        </Card>
      ))}
    </div>
  );
}

function SponsorManager({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-3 text-white/85 text-sm">
          <AlertCircle className="text-pink-brand flex-shrink-0" size={18} />
          <div>
            <strong>Come funziona:</strong> ogni sezione ha un titolo e può essere gestita in 2 modi:
            <ul className="list-disc ml-5 mt-2 space-y-1 text-xs text-white/70">
              <li><b>Immagine unica (PNG trasparente)</b> — carichi un'unica PNG con tutti i loghi già impaginati (comoda per mantenere layout personalizzato)</li>
              <li><b>Griglia singoli loghi</b> — aggiungi ogni partner uno alla volta, con nome, sottotitolo e logo individuale</li>
            </ul>
          </div>
        </div>
      </Card>

      <button className="btn-primary"><Plus size={16} /> Nuova sezione partner</button>

      {data.map((sec) => (
        <SponsorSectionEditor key={sec.id} section={sec} />
      ))}
    </div>
  );
}

function SponsorSectionEditor({ section }) {
  const [mode, setMode] = useState(section.modalita);
  return (
    <Card>
      {/* Titoli sezione */}
      <div className="space-y-3 pb-4 border-b border-white/10 mb-4">
        <div>
          <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block">Titolo sezione</label>
          <input defaultValue={section.titolo} className="w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white font-bold" />
        </div>
        <div>
          <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block">Sottotitolo (opzionale)</label>
          <input defaultValue={section.sottotitolo} className="w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
        </div>

        {/* Modalità toggle */}
        <div>
          <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-2 block">Modalità</label>
          <div className="flex gap-2 bg-navy-dark p-1 rounded-full border border-white/10 w-fit">
            <button
              onClick={() => setMode('image')}
              className={`py-2 px-4 rounded-full font-bold text-xs uppercase ${mode === 'image' ? 'bg-pink-brand text-white' : 'text-white/70'}`}
            >
              <ImageIcon size={12} className="inline mr-1" /> Immagine unica
            </button>
            <button
              onClick={() => setMode('grid')}
              className={`py-2 px-4 rounded-full font-bold text-xs uppercase ${mode === 'grid' ? 'bg-pink-brand text-white' : 'text-white/70'}`}
            >
              Griglia loghi
            </button>
          </div>
        </div>
      </div>

      {/* Contenuto in base alla modalità */}
      {mode === 'image' ? (
        <div>
          <UploadSlot label="PNG trasparente con tutti i loghi impaginati" accept="image/png,image/svg+xml" />
          {section.immagine && (
            <div className="mt-3 bg-white rounded-lg p-3">
              <img src={section.immagine} alt="" className="max-h-40 mx-auto" />
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/70 text-sm font-semibold">{section.items.length} partner nella griglia</span>
            <button className="btn-primary !py-2 !px-4 !text-xs"><Plus size={12} /> Aggiungi partner</button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {section.items.map((s) => (
              <div key={s.id} className="bg-navy-dark p-3 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></div>
                  <div className="flex-1 min-w-0">
                    <input defaultValue={s.name} className="w-full bg-transparent text-white font-semibold text-sm border-b border-white/10 pb-1 focus:outline-none focus:border-pink-brand" />
                    <input defaultValue={s.tagline} className="w-full bg-transparent text-white/60 text-xs mt-1 focus:outline-none" />
                  </div>
                  <button className="text-white/50 hover:text-red-400 p-1" aria-label="Rimuovi">×</button>
                </div>
                <UploadSlot label="Logo partner" accept="image/png,image/jpeg,image/svg+xml" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <button className="btn-primary !py-2 !px-5 !text-sm"><Save size={14} /> Salva</button>
        <button className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase hover:bg-red-500/25">
          Elimina sezione
        </button>
      </div>
    </Card>
  );
}

function GalleriaManager({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">Foto ({data.length})</h2>
          <button className="btn-primary !py-2 !px-4 !text-sm"><Upload size={14} /> Carica foto</button>
        </div>
        <UploadSlot label="Trascina più foto qui (supporta upload multiplo)" accept="image/*" />
      </Card>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.map((f) => (
          <div key={f.id} className="relative group">
            <img src={f.src} alt={f.titolo} className="w-full aspect-square object-cover rounded-xl" />
            <div className="absolute inset-0 bg-navy-dark/80 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
              <button className="p-2 bg-pink-brand rounded-full text-white"><Edit size={14} /></button>
            </div>
            <div className="mt-1 text-white/70 text-xs truncate">{f.titolo}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideoManager({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold">Video ({data.length})</h2>
          <button className="btn-primary !py-2 !px-4 !text-sm"><Upload size={14} /> Carica video</button>
        </div>
        <UploadSlot label="Carica file video o inserisci URL (YouTube/Facebook)" accept="video/mp4" />
      </Card>
      <div className="space-y-3">
        {data.map((v) => (
          <Card key={v.id}>
            <div className="flex items-center gap-3">
              <img src={v.thumbnail} alt={v.titolo} className="w-24 h-16 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm">{v.titolo}</div>
                <div className="text-white/60 text-xs">{v.descrizione}</div>
              </div>
              <button className="text-white/60 hover:text-pink-brand"><Edit size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EdizioniManager({ data }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-3 text-white/85">
          <AlertCircle className="text-pink-brand flex-shrink-0" size={20} />
          <span className="text-sm">Quando crei una nuova edizione, l'attuale verrà archiviata automaticamente qui.</span>
        </div>
      </Card>
      <button className="btn-primary"><Plus size={16} /> Nuova Edizione</button>
      <div className="grid md:grid-cols-2 gap-4">
        {data.map((ed) => (
          <Card key={ed.anno}>
            <div className="flex items-center justify-between mb-3">
              <span className="bg-pink-brand text-white font-bold px-3 py-1 rounded">{ed.anno}</span>
              <button className="text-white/60 hover:text-pink-brand"><Edit size={16} /></button>
            </div>
            <h3 className="text-white font-bold text-sm mb-1">{ed.edizione}</h3>
            <p className="text-white/60 text-xs">{ed.date}</p>
            <div className="flex items-center gap-2 mt-3 text-white/70 text-xs">
              <Trophy size={12} /> {ed.squadrePartecipanti} squadre
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MessaggiManager({ msgs }) {
  if (msgs.length === 0) {
    return <Card><div className="text-center py-8"><Mail size={40} className="text-white/30 mx-auto mb-3" /><p className="text-white/70">Nessun messaggio ricevuto</p></div></Card>;
  }
  return (
    <div className="space-y-3">
      {msgs.slice().reverse().map((m, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-bold">{m.nome}</h3>
            <span className="text-white/50 text-xs">{new Date(m.date).toLocaleString('it-IT')}</span>
          </div>
          <a href={`mailto:${m.email}`} className="text-pink-brand text-sm font-semibold">{m.email}</a>
          <p className="text-white/80 text-sm mt-2">{m.messaggio}</p>
        </Card>
      ))}
    </div>
  );
}

function GenericManager({ title, data }) {
  return (
    <Card>
      <h2 className="text-white font-bold text-lg mb-4">Modifica {title}</h2>
      <pre className="bg-navy-dark p-4 rounded-lg text-white/70 text-xs overflow-auto max-h-[500px]">{JSON.stringify(data, null, 2)}</pre>
      <p className="text-white/60 text-sm mt-4 flex items-start gap-2">
        <AlertCircle size={16} className="text-pink-brand flex-shrink-0 mt-0.5" />
        Editor completo disponibile dopo l'attivazione del backend.
      </p>
    </Card>
  );
}

function Impostazioni() {
  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-white font-bold text-lg mb-4">Cambio password</h2>
        <PasswordChangeForm />
      </Card>
      <Card>
        <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-2">Note di sicurezza</h3>
        <ul className="text-white/70 text-sm space-y-2 list-disc ml-5">
          <li>Cambia <strong>subito</strong> la password di default dopo la prima installazione.</li>
          <li>Usa una password lunga (12+ caratteri) e non condividerla via email.</li>
          <li>Dopo il cambio password, dovrai rifare il login con le nuove credenziali.</li>
        </ul>
      </Card>
    </div>
  );
}
